import {useEffect, useRef} from 'react';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {useNavigate} from '@common/ui/navigation/use-navigate';
import {useAuth} from '@common/auth/use-auth';
import {useSettings} from '@ui/settings/use-settings';
import {
  getBootstrapData,
  mergeBootstrapData,
} from '@ui/bootstrap-data/bootstrap-data-store';
import {toast} from '@ui/toast/toast';
import {setEchoSocketId} from '@common/http/get-echo-socket-id';
import {apiClient} from '@common/http/query-client';
import {useUserNotifications} from './dialog/requests/user-notifications';
import {BroadcastNotification} from './database-notification';

// ─── Echo singleton ───────────────────────────────────────────────────────────
let echoInstance: any | null = null;
let echoInitPromise: Promise<any | null> | null = null;

async function getEcho(
  pusherKey: string,
  pusherCluster: string,
): Promise<any | null> {
  if (echoInstance) return echoInstance;
  if (echoInitPromise) return echoInitPromise;

  echoInitPromise = (async () => {
    try {
      const [{default: Echo}, Pusher] = await Promise.all([
        import('laravel-echo'),
        import('pusher-js').then(m => m.default),
      ]);
      (window as any).Pusher = Pusher;

      echoInstance = new Echo({
        broadcaster: 'pusher',
        key: pusherKey,
        cluster: pusherCluster || 'mt1',
        forceTLS: true,
        authEndpoint: '/broadcasting/auth',
        auth: {headers: {'X-Requested-With': 'XMLHttpRequest'}},
      });

      const socketId = echoInstance?.socketId?.();
      if (socketId) setEchoSocketId(socketId);
      echoInstance?.connector?.pusher?.connection?.bind?.('connected', () => {
        setEchoSocketId(echoInstance.socketId());
      });

      return echoInstance;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Realtime (Echo) init failed:', err);
      echoInstance = null;
      return null;
    } finally {
      echoInitPromise = null;
    }
  })();

  return echoInitPromise;
}

// ─── Polling helper ───────────────────────────────────────────────────────────
async function fetchUnreadCount(): Promise<number> {
  const r = await apiClient.get('notifications/unread-count');
  return Number(r.data.count ?? 0);
}

const POLL_MS = 30_000;

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useRealtimeNotifications() {
  const {user, isLoggedIn} = useAuth();
  const settings = useSettings() as any;
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const pusherKey: string | undefined = settings?.realtime?.pusher_key;
  const pusherCluster: string = settings?.realtime?.pusher_cluster ?? 'mt1';

  // This ref tracks the last count we've already acted on.
  // It's initialised from bootstrap data so the first poll doesn't
  // false-trigger a toast for notifications already present at page-load.
  const prevCountRef = useRef<number>(
    user?.unread_notifications_count ?? 0,
  );

  // ── Polling — always active when logged in ────────────────────────────────
  const {data: polledCount} = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: fetchUnreadCount,
    enabled: isLoggedIn && !!user?.id,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
    staleTime: 0,
    gcTime: 0,
  });

  useEffect(() => {
    if (polledCount == null) return;

    const prev = prevCountRef.current;
    prevCountRef.current = polledCount;

    // Always keep the badge in sync with the server truth
    const bsData = getBootstrapData();
    if (
      bsData.user &&
      polledCount !== bsData.user.unread_notifications_count
    ) {
      mergeBootstrapData({
        user: {...bsData.user, unread_notifications_count: polledCount},
      });
    }

    // Only show a toast + refresh the list when the count went UP
    // (i.e. new notifications arrived since the last poll / echo event)
    if (polledCount > prev) {
      queryClient.invalidateQueries({queryKey: useUserNotifications.key});
      // Also refresh the sidebar delete-requests badge
      queryClient.invalidateQueries({queryKey: ['delete-requests', 'count']});
      // Refresh admin delete-requests list and user drive entries (file status)
      queryClient.invalidateQueries({
        queryKey: ['file-entries/delete-requests'],
      });
      queryClient.invalidateQueries({queryKey: ['drive']});
      toast('You have new notifications', {
        position: 'top-center',
        duration: 6000,
        action: {
          label: 'View',
          onExecute: () => navigate('/notifications'),
        },
      });
    }
  }, [polledCount, queryClient, navigate]);

  // ── Echo real-time — only when Pusher is configured ───────────────────────
  useEffect(() => {
    if (!isLoggedIn || !user?.id || !pusherKey) return;

    let cancelled = false;

    getEcho(pusherKey, pusherCluster).then(echo => {
      if (!echo || cancelled) return;

      const channel = echo.private(`App.User.${user.id}`);
      channel.notification((notif: BroadcastNotification) => {
        // Refresh the notification list immediately
        queryClient.invalidateQueries({queryKey: useUserNotifications.key});
        // Refresh the sidebar delete-requests badge for delete-request notifications
        if (
          notif.type ===
          'App\\Notifications\\FileDeleteRequestCreatedNotification'
        ) {
          queryClient.invalidateQueries({
            queryKey: ['delete-requests', 'count'],
          });
          // Refresh the admin delete-requests list so new row appears instantly
          queryClient.invalidateQueries({
            queryKey: ['file-entries/delete-requests'],
          });
        }
        // When a request is approved or declined, refresh the user's file grid
        // so the "Pending delete" badge appears/disappears without reload
        if (
          notif.type ===
            'App\\Notifications\\FileDeleteRequestApprovedNotification' ||
          notif.type ===
            'App\\Notifications\\FileDeleteRequestDeclinedNotification'
        ) {
          queryClient.invalidateQueries({queryKey: ['drive']});
        }

        // Update badge immediately without waiting for the next poll
        const bsData = getBootstrapData();
        if (bsData.user) {
          const newCount = (bsData.user.unread_notifications_count ?? 0) + 1;
          mergeBootstrapData({
            user: {...bsData.user, unread_notifications_count: newCount},
          });
          // Advance the ref so the next poll doesn't re-trigger the toast
          prevCountRef.current = newCount;
        }

        // Toast with the actual notification content
        const firstLine = notif?.lines?.[0]?.content;
        toast(firstLine || 'You have a new notification', {
          position: 'top-center',
          duration: 6000,
          action: notif.mainAction
            ? {
                label: notif.mainAction.label,
                onExecute: () => {
                  if (notif.mainAction?.action) {
                    navigate(notif.mainAction.action);
                  }
                },
              }
            : undefined,
        });
      });
    });

    return () => {
      cancelled = true;
      try {
        if (echoInstance && user?.id) {
          echoInstance.leave(`App.User.${user.id}`);
        }
      } catch {
        // noop
      }
    };
  }, [isLoggedIn, user?.id, pusherKey, pusherCluster, queryClient, navigate]);
}

